<template>
  <div class="timeline-top-bar">
    <div class="bar-left">
      <div class="logo">
        <v-icon color="#00d2d3" class="mr-2">mdi-video-check</v-icon>
        <span>换装时间轴联动控制台</span>
      </div>
    </div>

    <div class="bar-center text-center d-flex align-center">
      <div class="stats-text d-flex align-center">
        <!-- 共享剪辑池下拉 -->
        <v-menu location="bottom center" :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <v-btn color="cyan-lighten-4" size="x-small" variant="tonal" class="mr-3 px-3" v-bind="props">
              剪辑池 <strong>{{ selectedSegments.length }}</strong> 段片段
              <v-icon end size="14">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact" bg-color="#1e1e1e" theme="dark" width="300" max-height="400">
            <v-list-subheader>共享剪辑池 (组内长按拖动排序)</v-list-subheader>
            <v-divider></v-divider>

            <template v-for="group in poolGroups" :key="group.id">
              <v-list-subheader class="group-subheader">
                {{ group.name }}<span v-if="group.isCurrent">（当前）</span>
              </v-list-subheader>
              <draggable
                :model-value="group.segments"
                item-key="id"
                handle=".drag-handle"
                ghost-class="ghost"
                @update:model-value="$emit('reorder-group-clips', { groupIndex: group.groupIndex, segments: $event })"
              >
                <template #item="{ element }">
                <v-list-item 
                  @click="$emit('clip-jump', { start: element.start, transcription_id: element.transcription_id })"
                  class="cursor-pointer clip-item"
                >
                  <template v-slot:prepend>
                    <v-icon size="x-small" color="grey" class="drag-handle mr-2" @click.stop>mdi-drag-vertical</v-icon>
                    <div class="thumbnail-wrapper mr-2">
                      <v-img 
                        :src="thumbnailCache[element.id]" 
                        width="40" 
                        height="40" 
                        cover 
                        class="bg-grey-darken-4 rounded"
                      >
                        <template v-slot:placeholder>
                          <div class="d-flex align-center justify-center fill-height">
                            <v-progress-circular indeterminate size="10" width="1" color="grey"></v-progress-circular>
                          </div>
                        </template>
                      </v-img>
                    </div>
                  </template>
                  <v-list-item-title class="text-caption">
                    {{ formatTime(element.start) }} - {{ formatTime(element.end) }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-grey-lighten-1" style="font-size: 10px">
                    时长: {{ (element.end - element.start).toFixed(1) }}s
                  </v-list-item-subtitle>
                  <template v-slot:append>
                    <v-btn
                      icon="mdi-delete-outline"
                      variant="text"
                      size="x-small"
                      color="grey"
                      @click.stop="$emit('remove-clip', { groupIndex: group.groupIndex, segmentId: element.id })"
                    ></v-btn>
                  </template>
                </v-list-item>
                </template>
              </draggable>
            </template>
          </v-list>
        </v-menu>

        <span class="mr-3 text-caption text-grey">
          共计 <strong>{{ totalDuration.toFixed(1) }}</strong> 秒
        </span>
        <v-btn color="error" size="x-small" variant="tonal" class="mr-2 px-2" :disabled="loading || selectedSegments.length === 0" @click="$emit('clear-selection')">
          <v-icon start size="14">mdi-delete-sweep</v-icon>清空
        </v-btn>
        <v-btn color="success" size="x-small" variant="tonal" class="mr-2 px-2" :disabled="loading" @click="$emit('switch-to-pool')">
          <v-icon start size="14">mdi-swap-horizontal</v-icon>切到剪辑池
        </v-btn>
        <v-btn color="primary" size="x-small" class="px-2" :loading="loading" :disabled="selectedSegments.length === 0" @click="$emit('export-click')">
          <v-icon start size="14">mdi-export</v-icon>导出
        </v-btn>
      </div>
    </div>

    <div class="bar-right">
      <v-menu
        v-if="activeTasks && activeTasks.length > 0"
        open-on-hover
        open-on-click
      >
        <template v-slot:activator="{ props }">
          <v-btn
            :color="isAllCompleted ? 'success' : 'warning'"
            size="small"
            variant="tonal"
            class="mr-4"
            v-bind="props"
          >
            <v-icon start :class="{ 'mdi-spin': !isAllCompleted }">
              {{ isAllCompleted ? 'mdi-check-circle' : 'mdi-loading' }}
            </v-icon>
            <span v-if="isAllCompleted">任务已完成</span>
            <span v-else>{{ processingCount }} 个任务处理中...</span>
          </v-btn>
        </template>
        <v-list bg-color="#1e1e1e" theme="dark">
          <v-list-item
            v-for="(task, index) in activeTasks"
            :key="task.id || index"
            class="mb-1"
          >
            <v-list-item-title class="text-caption">
              {{ task.name }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              <span v-if="task.status === 'completed'" class="text-success">{{ task.message || '完成' }}</span>
              <span v-else-if="task.status === 'failed'" class="text-error">{{ task.message || '失败' }}</span>
              <span v-else class="text-warning">{{ task.message || '排队中...' }}</span>
            </v-list-item-subtitle>
            <template v-slot:append>
              <v-btn
                v-if="task.file_url"
                size="x-small"
                color="success"
                variant="tonal"
                @click.stop="$emit('download-file', task.file_url)"
              >
                下载
              </v-btn>
            </template>
          </v-list-item>
          <v-list-item class="d-flex justify-center" @click="$emit('open-progress-dialog')">
            <v-btn size="small" variant="text" color="primary">查看详情</v-btn>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn size="small" variant="tonal" class="mr-4" :disabled="loading" @click="$emit('history-click')">
        <v-icon start>mdi-history</v-icon>切片历史
      </v-btn>
      
      <span class="user-name mx-2">你好，{{ phoneNumber }}</span>
      
      <v-btn text color="primary" size="small" @click="logout">
        退出
      </v-btn>
      
      <v-btn icon="mdi-home" size="small" variant="text" class="ml-2" @click="$router.push('/clipper')"></v-btn>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import draggable from 'vuedraggable'

const props = defineProps({
  isPlaying: Boolean,
  loading: Boolean,
  transcriptionId: String,
  poolGroups: {
    type: Array,
    default: () => []
  },
  selectedSegments: {
    type: Array,
    default: () => []
  },
  activeTasks: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['export-click', 'switch-to-pool', 'history-click', 'toggle-play', 'clear-selection', 'download-file', 'open-progress-dialog', 'clip-jump', 'remove-clip', 'reorder-group-clips'])

import { ref, watch, onUnmounted } from 'vue'
import axios from 'axios'
import { BASE_URL } from '@/config'

const thumbnailCache = ref({})

const fetchThumbnail = async (seg) => {
  const tid = seg.transcription_id || props.transcriptionId
  if (!tid || thumbnailCache.value[seg.id]) return
  
  const sec = Math.floor(seg.start)
  const url = `${BASE_URL}api/clothing-timeline/frame/?transcription_id=${tid}&sec=${sec}`
  
  try {
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    })
    const blobUrl = URL.createObjectURL(response.data)
    thumbnailCache.value[seg.id] = blobUrl
  } catch (e) {
    console.error('Failed to fetch thumbnail', e)
  }
}

watch(() => props.selectedSegments, (newList) => {
  newList.forEach(seg => {
    if (seg.id && !thumbnailCache.value[seg.id]) {
      fetchThumbnail(seg)
    }
  })
}, { immediate: true, deep: true })

onUnmounted(() => {
  Object.values(thumbnailCache.value).forEach(url => URL.revokeObjectURL(url))
})

const router = useRouter()
const store = useStore()

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 10)
  
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms}`
  }
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms}`
}

const totalDuration = computed(() => {
  return props.selectedSegments.reduce((acc, seg) => acc + Math.max(0, seg.end - seg.start), 0)
})

const phoneNumber = computed(() => {    
  let user = JSON.parse(localStorage.getItem('user'))
  if (user && user.phoneNumber) {
    let covered_phone =  user.phoneNumber
    return covered_phone.slice(0,3)+'****'+covered_phone.slice(7,11)
  } else {
    return '未知用户'
  }    
})

const logout = () => {
  store.dispatch('logout')
  router.push('/login/')
}

const processingCount = computed(() => {
  return props.activeTasks.filter(t => t.status !== 'completed' && t.status !== 'failed').length
})

const isAllCompleted = computed(() => {
  return props.activeTasks.length > 0 && processingCount.value === 0
})
</script>

<style scoped>
.ghost {
  opacity: 0.5;
  background: #2c2c2c !important;
}

.clip-item:hover .drag-handle {
  color: #00d2d3 !important;
}

.drag-handle {
  cursor: grab;
  padding: 4px;
}

.drag-handle:active {
  cursor: grabbing;
}

.thumbnail-wrapper {
  width: 40px;
  height: 40px;
  background-color: #000;
  border: 1px solid #333;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.timeline-top-bar {
  height: 48px;
  background-color: #181818;
  border-bottom: 1px solid #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  color: #efefef;
}

.logo {
  font-weight: bold;
  font-size: 16px;
  display: flex;
  align-items: center;
}

.bar-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.bar-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.stats-text {
  font-size: 13px;
  color: #a0d8ef;
  background: rgba(0, 210, 211, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 210, 211, 0.3);
}

.bar-right {
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
}

.user-name {
  font-size: 13px;
  color: #ccc;
}
</style>
